import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { QuestionBlocks, Answers, Question } from './Questionnaire';

interface Template {
  id: number;
  cycle_id: number;
  name: string;
  description?: string | null;
  respondent_layer: string;
  about_role: string;
  questions: Question[];
}

// What the "עבור:" line would show for a real respondent of this questionnaire
const subjectPlaceholder: Record<string, string | null> = {
  self: '[שם הממלא/ת – הערכה עצמית]',
  manager: '[שם המנהל/ת הישיר/ה]',
  employee: '[שם העובד/ת]',
  hq: '[שם איש/אשת המטה]',
  ceo: 'מנהלת הרשות',
  org_survey: null,
};

/** Admin-only preview: renders a questionnaire exactly as a respondent sees it. Nothing is saved. */
export default function AdminPreview() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [tpl, setTpl] = useState<Template | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    api.get(`/questionnaires/${templateId}`)
      .then(({ data }) => setTpl(data))
      .catch(() => navigate('/admin/cycles'));
  }, [templateId, navigate]);

  const setAnswer = (questionId: number, field: string, value: any) => {
    setAnswers(prev => ({ ...prev, [questionId]: { ...prev[questionId], [field]: value } }));
  };

  const toggleChecklist = (questionId: number, option: string) => {
    const current = answers[questionId]?.answer_checklist || [];
    const updated = current.includes(option) ? current.filter(o => o !== option) : [...current, option];
    setAnswer(questionId, 'answer_checklist', updated);
  };

  const simulateSubmit = () => {
    setError('');
    setNotice('');
    const missing = tpl!.questions.filter(q => {
      if (!q.is_required) return false;
      const ans = answers[q.id];
      if (!ans) return true;
      if (q.question_type === 'open_text' && !ans.answer_text?.trim()) return true;
      if ((q.question_type === 'rating' || q.question_type === 'rating_text') && !ans.answer_rating) return true;
      if (q.question_type === 'rating_text' && !ans.answer_text?.trim()) return true;
      return false;
    });
    if (missing.length > 0) {
      setError(`יש למלא את כל השדות המסומנים כחובה (${missing.length} שדות חסרים)`);
    } else {
      setNotice('בשאלון אמיתי ההגשה הייתה מתבצעת כעת. בתצוגה מקדימה לא נשמר דבר.');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!tpl) return <div className="loading"><div className="spinner" />טוען תצוגה מקדימה...</div>;

  const subject = subjectPlaceholder[tpl.about_role];

  return (
    <div className="page page-narrow">
      <div className="alert alert-info" style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span><strong>תצוגה מקדימה</strong> – כך ייראה השאלון לממלא/ת. התשובות כאן אינן נשמרות.</span>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/cycles/${tpl.cycle_id}/questionnaires`)}>← חזרה לעריכה</button>
      </div>

      <div className="page-header">
        <h1 style={{ marginTop: '12px' }}>{tpl.name}</h1>
        {subject && <p className="text-muted">עבור: {subject}</p>}
        {tpl.description && (
          <div className="alert alert-info" style={{ marginTop: '12px', whiteSpace: 'pre-wrap', textAlign: 'right' }}>
            {tpl.description}
          </div>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      {tpl.questions.length === 0 ? (
        <div className="empty-state"><div className="empty-state-icon">📝</div><h3>אין שאלות בשאלון זה עדיין</h3></div>
      ) : (
        <QuestionBlocks questions={tpl.questions} answers={answers} setAnswer={setAnswer} toggleChecklist={toggleChecklist} isReadonly={false} />
      )}

      <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={() => { setError(''); setNotice('בתצוגה מקדימה לא נשמרת טיוטה.'); }}>
          שמור כטיוטה
        </button>
        <button className="btn btn-success btn-lg" onClick={simulateSubmit}>
          הגש שאלון
        </button>
      </div>
    </div>
  );
}
